import React from 'react'
import {useSelector} from 'react-redux'
import {
  CatalogWrapper,
  ProductsPanel,
  CatalogLoadingOverlay,
  Header,
  BrandSection,
  LogoMark,
  TitleGroup,
  Title,
  Subtitle,
  Actions,
} from './ProductsCatalog.styles'
import {Spinner} from '../../../../../components/loader-spinner'
import Icon from '../../../../../core/shared/Icon'
import {CatalogGrid} from '../../../../../features/CatalogGrid'
import {isPartnerChannelRole} from '../../../../../features/CatalogGrid/utils/isPartnerChannelRole'
import {selectEffectiveRole} from '../../../../../app-store/selector'
import {RetainWebsiteDowngradeModal} from '../../../../../components/catalog-components/retain-website-downgrade-modal'
import {
  selectAccountInfo,
  selectActiveLocation,
  selectSelectedLocationId,
  selectOrderMode,
  selectProductConfigInfo,
} from '../../../../../app-store/PurchaseLinkSlice'
import {LocationSelector} from '../components/LocationSelector'
import {ParentPayLocationSelector} from '../components/ParentPayLocationSelector'

import {usePurchaseLinkProducts} from '../hooks/usePurchaseLinkProducts'
import {usePurchaseLinkLocation} from '../hooks/usePurchaseLinkLocation'

// Analog of ProposalTool's ProductsCatalog page — same header design (icon
// badge + title/subtitle, kept as a separate, deliberately duplicated style
// set rather than a shared component — see ProductsCatalog.styles.js), but
// different header actions: no Back button (the Catalog Selection step's own
// footer handles navigation) and no Save/Preview/Send actions (checkout/link
// submission is out of scope for this flow — see OrderSummary for where that
// would eventually be wired in) — just whichever location selector applies.
//
// Rendered separately from ProductsCatalog below (see AccountSetup's
// CatalogSelectionStep) so it can sit outside that step's StepCard, on the
// plain page background, matching every other step's bare header — it reads
// its own account/location state directly rather than taking props, exactly
// like ProductsCatalog does.
export function ProductsCatalogHeader() {
  const accountInfo = useSelector(selectAccountInfo)
  const activeLocation = useSelector(selectActiveLocation)
  const orderMode = useSelector(selectOrderMode)
  const isParentPay = orderMode === 'parentPay'
  const activeLocationEmail =
    activeLocation?.recurlyAccountInfo?.accountInfo?.email ||
    activeLocation?.email

  return (
    <Header>
      <BrandSection>
        <LogoMark aria-hidden="true">
          <Icon name="product-catalog" size="lg" />
        </LogoMark>
        <TitleGroup>
          <Title>Product Catalog</Title>
          <Subtitle>
            {activeLocation?.locationName || accountInfo?.businessName}
            {activeLocationEmail && <span> | {activeLocationEmail}</span>}
          </Subtitle>
        </TitleGroup>
      </BrandSection>
      <Actions>
        {isParentPay ? <ParentPayLocationSelector /> : <LocationSelector />}
      </Actions>
    </Header>
  )
}

export function ProductsCatalog() {
  const selectedLocationId = useSelector(selectSelectedLocationId)
  const productConfigInfo = useSelector(selectProductConfigInfo)
  const isPartnerChannel = isPartnerChannelRole(
    useSelector(selectEffectiveRole),
  )
  const {loading: isLocationLoading} = usePurchaseLinkLocation()
  const {
    softwareGroups,
    addonGroups,
    alternatePlanCodesToHide,
    allPlansUnfiltered,
    isDataLoaded,
    selectedCountry,
    selectedProducts,
    selectedProductsByCode,
    disabledPlanCodes,
    validExtensionList,
    lockedBillingByGroup,
    showMCGroupAnnualToggle,
    isSocialContentGenGrandfathered,
    showGrowthPackageOnboarding,
    handleAddProduct,
    handleUpdateProduct,
    handleQuantityChange,
    pendingWebsiteDowngradePrompt,
    handleWebsiteDowngradeAnswer,
  } = usePurchaseLinkProducts()

  const isLocked = isLocationLoading

  return (
    <CatalogWrapper>
      <ProductsPanel>
        {isLocked && (
          <CatalogLoadingOverlay>
            <Spinner />
          </CatalogLoadingOverlay>
        )}
        <CatalogGrid
          key={selectedLocationId}
          softwareGroups={softwareGroups
            .map(group => ({
              ...group,
              plans: (group.plans ?? []).filter(
                plan => !alternatePlanCodesToHide.has(plan.code),
              ),
            }))
            .filter(group => group.plans.length > 0)}
          addonGroups={addonGroups}
          allPlans={allPlansUnfiltered}
          productConfigInfo={productConfigInfo}
          isDataLoaded={isDataLoaded}
          selectedCountry={selectedCountry}
          selectedProducts={selectedProducts}
          selectedProductsByCode={selectedProductsByCode}
          disabledPlanCodes={disabledPlanCodes}
          validExtensionList={validExtensionList}
          lockedBillingByGroup={lockedBillingByGroup}
          showMCGroupAnnualToggle={showMCGroupAnnualToggle}
          isSocialContentGenGrandfathered={isSocialContentGenGrandfathered}
          showGrowthPackageOnboarding={showGrowthPackageOnboarding}
          minColumns={3}
          combineNonKeapAddonTabs={isPartnerChannel}
          onAddProduct={handleAddProduct}
          onUpdateProduct={handleUpdateProduct}
          onQuantityChange={handleQuantityChange}
        />
      </ProductsPanel>

      <RetainWebsiteDowngradeModal
        isOpen={pendingWebsiteDowngradePrompt !== null}
        downgradedFrom={pendingWebsiteDowngradePrompt?.downgradedFromName}
        onConfirm={handleWebsiteDowngradeAnswer}
      />
    </CatalogWrapper>
  )
}
